/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback } from "react";
import { useTranslate } from "@adapptio/lib-components";
import {
	isModel,
	isSchemaFlowNodeListModel,
	isSchemaFlowNodeModel,
	TGenericModelNode
} from "@adapptio/lib-blueprint";
import { isFunction } from "@adapptio/lib-shared";
import { InspectSingleNode } from "../../../ui/components/Inspector/InspectSingleNode";
import { isSelectionItemEndpointFlowNode, TEndpointEditSelectionItem } from "./EndpointEditorEditSelection";
import { InspectFlowNode } from "../../../ui/components/Inspector/InspectFlowNode";
import { EndpointEditorEditView } from "../EndpointEditorEdit/EndpointEditorEditView";

export interface IInspectEndpointSelectionItemProps {
	selectionItem: TEndpointEditSelectionItem;
	getNodeById: (nodeId: number) => TGenericModelNode;
	view: EndpointEditorEditView;
}

const pathFlowNode = [ "$flowNode" ];
const pathNode = [ "$node" ];

export const InspectEndpointSelectionItem: React.FC<IInspectEndpointSelectionItemProps> = ({
	selectionItem,
	getNodeById,
	view
}) => {
	const t = useTranslate();

	const getModelNodeById = useCallback<IInspectEndpointSelectionItemProps["getNodeById"]>(
		(nodeId) => {
			if (!isFunction(getNodeById)) {
				console.error("Property 'getNodeById' is not a function.");
				return null;
			}

			return getNodeById(nodeId);
		},
		[ getNodeById ]
	);

	if (isSelectionItemEndpointFlowNode(selectionItem)) {
		const nodeList = getModelNodeById(selectionItem?.nodeListModelId);
		const node = getModelNodeById(selectionItem?.nodeId);

		if (isSchemaFlowNodeModel(node) && isSchemaFlowNodeListModel(nodeList)) {
			return <InspectFlowNode model={node} nodeList={nodeList} depth={0} path={pathFlowNode} />;
		}
	}

	const selectedModel = getModelNodeById(selectionItem?.nodeId);

	if (isModel(selectedModel)) {
		return <InspectSingleNode model={selectedModel} depth={0} path={pathNode} />;
	}

	return null;
};
